import { ApolloClient, HttpLink, InMemoryCache } from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import { createAzureInsightsLink } from "./azureInsightsLink"
import { checkCacheSize, shouldTriggerCacheGC } from "./cachePressure"
import {
  createErrorContext,
  createIsraeliTimestamp,
  logGraphQLErrors,
  logNetworkError,
} from "./errorLink"
import { getGraphQLUrl } from "./graphqlUrl"
import generatedIntrospection from "./introspection-result.json"
import {
  checkMemoryThreshold,
  logCacheStats,
  logMemoryStats,
  shouldCollectMemoryTelemetry,
} from "./memoryMonitor"

const GRAPHQL_URL = getGraphQLUrl()

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => !!error,
  },
})

const errorLink = onError(({ graphQLErrors, operation, networkError }) => {
  try {
    const errorContext = createErrorContext(operation, GRAPHQL_URL)

    if (graphQLErrors) {
      logGraphQLErrors(graphQLErrors, errorContext)
    }

    if (networkError) {
      logNetworkError(networkError, errorContext)
    }
  } catch (errorHandlingError) {
    // Fallback error logging if our error handling fails
    console.error("=== ERROR HANDLER FAILED ===")
    console.error("Error handling error:", errorHandlingError)
    console.error("Original GraphQL errors:", graphQLErrors)
    console.error("Original network error:", networkError)
    console.error("Operation:", operation)
    console.error("GraphQL URL:", GRAPHQL_URL)
    console.error("Timestamp:", createIsraeliTimestamp())
    console.error("==============================")
  }
})

// Create Azure Insights link instance
const azureInsightsLink = createAzureInsightsLink()

// Singleton instance for server-side usage to prevent memory leaks
let serverClientInstance: ApolloClient<any> | null = null
let serverRequestCount = 0
const MAX_REQUESTS_BEFORE_RESET = 1000 // Reset cache after 1000 requests to prevent unbounded growth
const MEMORY_LOG_INTERVAL = 100 // Log memory stats every 100 requests
const CACHE_CHECK_INTERVAL = 100 // Check cache pressure every 100 requests

// Create a function that returns the client for server-side usage
export function getClient() {
  // In browser, always create new client (handled by apollo-provider.tsx)
  if (typeof window !== "undefined") {
    return new ApolloClient({
      cache: new InMemoryCache({
        possibleTypes: generatedIntrospection.possibleTypes,
        resultCaching: false,
      }),
      link: retryLink
        .concat(azureInsightsLink)
        .concat(errorLink)
        .concat(
          new HttpLink({
            uri: GRAPHQL_URL,
          }),
        ),
      defaultOptions: {
        query: {
          errorPolicy: "all",
        },
      },
    })
  }

  // On server, reuse singleton instance
  if (!serverClientInstance) {
    serverClientInstance = new ApolloClient({
      cache: new InMemoryCache({
        possibleTypes: generatedIntrospection.possibleTypes,
        resultCaching: false, // Disable for build time
        // Add cache size limit to prevent unbounded growth
        typePolicies: {
          Query: {
            fields: {
              // Limit cache entries per query field
              components: {
                merge: false, // Don't merge, replace to prevent accumulation
              },
              content: {
                merge: false,
              },
            },
          },
        },
      }),
      link: retryLink
        .concat(azureInsightsLink)
        .concat(errorLink)
        .concat(
          new HttpLink({
            uri: GRAPHQL_URL,
          }),
        ),
      // Add Azure Insights telemetry first
      defaultOptions: {
        query: {
          errorPolicy: "all",
          fetchPolicy: "no-cache",
        },
      },
    })
    serverRequestCount = 0
  }

  // Periodically clear cache to prevent unbounded memory growth
  serverRequestCount++

  // Log memory stats and check cache pressure periodically if monitoring is enabled
  if (
    shouldCollectMemoryTelemetry() &&
    serverRequestCount % MEMORY_LOG_INTERVAL === 0
  ) {
    logMemoryStats(`Apollo Client - Request ${serverRequestCount}`)
    checkMemoryThreshold(`Apollo Client - Request ${serverRequestCount}`)
  }

  // Check cache pressure and trigger gc if needed
  if (serverRequestCount % CACHE_CHECK_INTERVAL === 0) {
    try {
      if (shouldTriggerCacheGC(serverClientInstance.cache as InMemoryCache)) {
        const cacheMetrics = checkCacheSize(
          serverClientInstance.cache as InMemoryCache,
        )

        if (shouldCollectMemoryTelemetry()) {
          logCacheStats(
            `Request ${serverRequestCount} - Before GC`,
            cacheMetrics,
          )
        }

        // Trigger garbage collection
        serverClientInstance.cache.gc()

        if (shouldCollectMemoryTelemetry()) {
          const afterGC = checkCacheSize(
            serverClientInstance.cache as InMemoryCache,
          )
          logCacheStats(`Request ${serverRequestCount} - After GC`, afterGC)
          console.log(
            `[Apollo] Cache GC triggered at request ${serverRequestCount}`,
          )
        }
      }
    } catch (error) {
      console.error("[Apollo] Failed to check/trigger cache GC:", error)
    }
  }

  if (serverRequestCount >= MAX_REQUESTS_BEFORE_RESET) {
    try {
      // Log memory and cache stats before reset
      if (shouldCollectMemoryTelemetry()) {
        logMemoryStats("Apollo Client - Before Cache Reset")
        const cacheMetrics = checkCacheSize(
          serverClientInstance.cache as InMemoryCache,
        )
        logCacheStats("Before Cache Reset", cacheMetrics)
      }

      serverClientInstance.cache.reset()
      serverRequestCount = 0
      console.log(
        "[Apollo] Server cache cleared after",
        MAX_REQUESTS_BEFORE_RESET,
        "requests",
      )

      // Log memory and cache stats after reset
      if (shouldCollectMemoryTelemetry()) {
        logMemoryStats("Apollo Client - After Cache Reset")
        const cacheMetrics = checkCacheSize(
          serverClientInstance.cache as InMemoryCache,
        )
        logCacheStats("After Cache Reset", cacheMetrics)
      }
    } catch (error) {
      console.error("[Apollo] Failed to reset cache:", error)
    }
  }

  return serverClientInstance
}

/**
 * Manually reset the server-side Apollo client cache
 * Useful for testing or manual memory management
 */
export function resetServerCache() {
  if (serverClientInstance && typeof window === "undefined") {
    try {
      // Log memory and cache stats before manual reset
      if (shouldCollectMemoryTelemetry()) {
        logMemoryStats("Apollo Client - Before Manual Cache Reset")
        const cacheMetrics = checkCacheSize(
          serverClientInstance.cache as InMemoryCache,
        )
        logCacheStats("Before Manual Cache Reset", cacheMetrics)
      }

      serverClientInstance.cache.reset()
      serverRequestCount = 0
      console.log("[Apollo] Server cache manually reset")

      // Log memory and cache stats after manual reset
      if (shouldCollectMemoryTelemetry()) {
        logMemoryStats("Apollo Client - After Manual Cache Reset")
        const cacheMetrics = checkCacheSize(
          serverClientInstance.cache as InMemoryCache,
        )
        logCacheStats("After Manual Cache Reset", cacheMetrics)
      }

      return true
    } catch (error) {
      console.error("[Apollo] Failed to manually reset cache:", error)
      return false
    }
  }
  return false
}

/**
 * Get current server-side request count
 * Useful for monitoring and debugging
 */
export function getServerRequestCount(): number {
  return serverRequestCount
}
